import { isFree, type AvailableUnit, type HeldGarment } from './rail';
import { formatDocumentDate } from '../format/dates';
import { scopedScan } from '../scan';
import { normalizeReference } from '../units/referenceNumber';

/**
 * What a scan says while picking garments for a booking — the rail's version of the hand-over's
 * `handoverScan`. Three different mistakes get three sentences: a garment that is spoken for on these
 * dates (ask the other customer, or pick another), and a tag that is not on this branch's rail at all
 * (wrong shop, or a design the rail did not search).
 */
export type RailScan = { tone: 'good'; unit: AvailableUnit } | { tone: 'bad'; message: string };

export function railScan(raw: string, rail: AvailableUnit[]): RailScan | null {
  let result = scopedScan(raw, rail, isFree);

  // Not a code on the rail: the shop's own number typed whole ("7", "RN-7") is the garment it is
  // on. The QR carries the serial alone, so a scan never lands here.
  if (result.outcome === 'elsewhere') {
    const reference = normalizeReference(raw);
    const numbered = reference
      ? rail.find((unit) => unit.referenceNumber === reference)
      : undefined;

    if (numbered) {
      result = scopedScan(numbered.serialCode, rail, isFree);
    }
  }

  switch (result.outcome) {
    case 'empty':
      return null;
    case 'matched':
      return { tone: 'good', unit: result.garment };
    case 'ineligible':
      return {
        tone: 'bad',
        message: `${result.garment.serialCode} is not free for these dates — ${heldLine(result.garment.heldBy ?? null)}.`,
      };
    case 'elsewhere':
      return { tone: 'bad', message: `${result.term} is not on this branch's rail.` };
  }
}

/** "with Raj Shah until Thu, 19 Nov 2026" — who to ask, or the reason, and when it frees. */
export function heldLine(held: HeldGarment | null): string {
  if (!held) {
    return 'spoken for';
  }

  const who = held.holder ? `with ${held.holder.customerName}` : held.reason.toLowerCase();
  const until = held.untilDate ? ` until ${formatDocumentDate(held.untilDate)}` : '';

  return `${who}${held.tentative ? ' (on hold)' : ''}${until}`;
}
